///
/// EarthBrowser
///
/// Created by Matt Giger on 1/29/2025
/// Copyright (c) 2025 Geodata Labs
///

import { nativeRequestHeaders } from '@/native/device'
import { getDeviceId } from '@/utils/deviceId'
import { logger } from '@/utils/LogDispatcher'
import {
	type BaseQueryFn,
	type FetchArgs,
	fetchBaseQuery,
	type FetchBaseQueryError
} from '@reduxjs/toolkit/query/react'
import { Mutex } from 'async-mutex'
import { type RootState } from '../store'
import { setUserProfile } from '../userSlice'

const mutex = new Mutex()
let isRefreshing = false
let lastRefreshAttempt = 0
const REFRESH_COOLDOWN_MS = 5000 // Don't retry refresh more than once per 5 seconds
const cAuthRefreshURL = '/api/auth/refresh'

// Base query with reauth for expired access tokens
export const bearerTokenQuery: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (args, api, extraOptions) => {

	// If a refresh is in progress, wait for it to complete before proceeding
	if (isRefreshing) {
		await mutex.waitForUnlock()
	}

	// Make the request with the current token
	let result = await accessTokenQuery(args, api, extraOptions)

	// Check if we got a 401 unauthorized error and the user is signed in
	// Access the Redux store to check if userProfile is set
	const error = result.error
	const isSignedIn = (api.getState() as RootState).user.userProfile !== undefined
	if (error?.status === 401 && isSignedIn) {
		const now = Date.now()
		const timeSinceLastRefresh = now - lastRefreshAttempt

		// At launch every persisted-session query fires at once against an
		// expired access token, so a burst of 401s lands together. Exactly
		// one of them performs the refresh; every other one must ride that
		// refresh and retry, never surface the 401 (a dropped request leaves
		// its RTK Query entry rejected until something remounts it, which is
		// how the paid entitlement used to stay free until the profile
		// dialog opened). The mutex check therefore comes BEFORE the
		// cooldown check.
		if (mutex.isLocked()) {
			// Another request is already refreshing the token: wait for it,
			// then retry with the new token
			await mutex.waitForUnlock()
			result = await accessTokenQuery(args, api, extraOptions)
		} else if (timeSinceLastRefresh > REFRESH_COOLDOWN_MS) {
			// Acquire the mutex to prevent other requests from proceeding
			const release = await mutex.acquire()
			isRefreshing = true

			try {
				lastRefreshAttempt = now

				// Attempt to refresh the token
				const refreshResult = await refreshTokenQuery(cAuthRefreshURL, api, extraOptions)

				if (refreshResult.data) {
					// Token refreshed - retry the original request once
					result = await accessTokenQuery(args, api, extraOptions)
				} else {
					api.dispatch(setUserProfile(undefined))
				}
			} finally {
				isRefreshing = false
				release()
			}
		} else {
			// A refresh completed within the cooldown and none is in flight:
			// this request was almost certainly sent with the stale token
			// before the refresh landed. Retry once with the current token.
			// The retry is a bare fetch, not re-run through this handler, so
			// a second 401 is returned as-is and cannot loop into another
			// refresh.
			logger.info(`Retrying 401 after recent refresh (${timeSinceLastRefresh}ms ago)`)
			result = await accessTokenQuery(args, api, extraOptions)
		}
	}

	return result
}

// Validate API URL at startup
const apiUrl = import.meta.env.VITE_API_URL
if (!apiUrl) {
	logger.error('VITE_API_URL environment variable is not set')
	console.error('CRITICAL: VITE_API_URL environment variable is not set. API requests will fail.')
} else {
	logger.info(`API URL configured: ${apiUrl}`)
}

// Stable per-browser device id sent on every request so session-creating
// endpoints (sign-in, token refresh, session restore) can match this browser
// to its device record for seat enforcement. The Capacitor apps also
// identify their platform and device model (no-op headers on the web)
function attachDeviceId(headers: Headers): Headers {
	headers.set('X-Device-Id', getDeviceId())
	for (const [name, value] of Object.entries(nativeRequestHeaders())) {
		headers.set(name, value)
	}
	return headers
}

// Base queries WITHOUT retry - we handle retries manually in bearerTokenQuery
const accessTokenQuery = fetchBaseQuery({
	baseUrl: apiUrl || 'https://api.earthbrowser.com',
	credentials: 'include', // Required to send cookies with cross-origin requests
	prepareHeaders: attachDeviceId,
})

const refreshTokenQuery = fetchBaseQuery({
	baseUrl: apiUrl || 'https://api.earthbrowser.com',
	credentials: 'include', // Required to send cookies with cross-origin requests
	prepareHeaders: attachDeviceId,
})